// Canonical RBAC constants and permission-check logic, shared by apps/api
// and apps/web. Before this, hasPermission() was independently reimplemented
// in apps/api/src/middleware/authorize.ts and apps/web/src/lib/permissions.ts
// — same PEOPLE/USERS alias case, hand-copied rather than shared — and ~25
// call sites across both apps compared against raw 'SUPERADMIN'/'OWNER'/etc.
// string literals instead of a shared constant.
//
// This package has zero runtime dependencies, so it's safe to import from
// both the Node API and the browser-bundled web app.

export const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
  VIEWER: 'VIEWER',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

// Lifted verbatim from packages/db/src/seed-goodday-users.ts's
// ALL_MODULES/ALL_ACTIONS — same values, same order.
export const MODULES = {
  DASHBOARD: 'DASHBOARD',
  LEADS: 'LEADS',
  DEALS: 'DEALS',
  // SALES_DOCS remains the umbrella module for the shared sales-doc lifecycle
  // machinery (submit/resubmit/request-proforma, sales-doc attachments) —
  // those stay gated on it unchanged.
  // QUOTATIONS/PROFORMA_INVOICES/INVOICE_REQUESTS split out specifically the
  // "which document type can this user directly CREATE or reach the list
  // page for" decision, which SALES_DOCS alone couldn't express: a Sales Rep
  // needs SALES_DOCS to use their own quotation's lifecycle, but should only
  // be able to originate a new Quotation, not a standalone Proforma/Invoice.
  SALES_DOCS: 'SALES_DOCS',
  QUOTATIONS: 'QUOTATIONS',
  PROFORMA_INVOICES: 'PROFORMA_INVOICES',
  INVOICE_REQUESTS: 'INVOICE_REQUESTS',
  TASKS: 'TASKS',
  SPACES: 'SPACES',
  PROJECTS: 'PROJECTS',
  PEOPLE: 'PEOPLE',
  USERS: 'USERS',
  ACCOUNTS: 'ACCOUNTS',
  // Everyone's own timesheet. Who approves is decided by role and department, and reports are
  // Super Admin only, so VIEW is the only action that is checked.
  TIMESHEETS: 'TIMESHEETS',
  NOTES: 'NOTES',
} as const

export type ModuleName = (typeof MODULES)[keyof typeof MODULES]

export const ACTIONS = {
  VIEW: 'VIEW',
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  ASSIGN: 'ASSIGN',
} as const

export type ActionName = (typeof ACTIONS)[keyof typeof ACTIONS]

export interface Permission {
  module: string
  actions: string[]
}

export function isSuperAdminOrOwner(role?: string | null): boolean {
  return role === ROLES.SUPERADMIN || role === ROLES.OWNER
}

/**
 * Core permission check, shared by both apps. SUPERADMIN/OWNER always pass.
 * PEOPLE and USERS are treated as aliases of each other, since the two
 * modules have always been used interchangeably across this codebase.
 */
export function hasPermission(
  entity: { role?: string | null; permissions?: Permission[] | null },
  module: string,
  action: string
): boolean {
  if (isSuperAdminOrOwner(entity.role)) return true
  const permissions = entity.permissions || []
  const entry = permissions.find(
    (p) =>
      p.module === module ||
      (module === MODULES.PEOPLE && p.module === MODULES.USERS) ||
      (module === MODULES.USERS && p.module === MODULES.PEOPLE)
  )
  // Timesheets: all authenticated employees can view/log their timesheet by default
  // unless explicitly restricted.
  if (module === MODULES.TIMESHEETS && action === ACTIONS.VIEW) {
    if (!entry) return true
    return entry.actions.includes('*') || entry.actions.includes(action)
  }
  if (!entry) return false
  if (entry.actions.includes('*')) return true
  return entry.actions.includes(action)
}

// Which configured department (GoodDayDepartment.name) does the work behind
// each service category. A project's `departments` is derived from its
// services with this, so the right department's admin can see it.
export const DEPARTMENT_BY_SERVICE_CATEGORY: Record<string, string> = {
  TECHNOLOGY: 'Development',
  'MARKETING & GROWTH': 'Creative & Marketing',
  CREATIVE: 'Creative & Marketing',
}

// Creative and Marketing are one department. Older data (and older clients) still say "Creative" or
// "Marketing"; this turns either into the one name so nothing is lost while that data is moved over.
export const CREATIVE_MARKETING_DEPARTMENT = 'Creative & Marketing'

const CREATIVE_MARKETING_NAMES = new Set([
  'creative',
  'marketing',
  'creative department',
  'marketing department',
  'creative & marketing',
  'creative and marketing',
])

export function canonicalDepartmentName<T extends string | null | undefined>(name: T): T | string {
  const trimmed = typeof name === 'string' ? name.trim() : name
  if (typeof trimmed === 'string' && CREATIVE_MARKETING_NAMES.has(trimmed.toLowerCase())) return CREATIVE_MARKETING_DEPARTMENT
  return trimmed
}
